// external
import React, { useState, useRef, useCallback, useEffect } from "react";
import _ from "lodash";
import classNames from "classnames";

import { useI18n } from "Hooks";
import { useDidUpdate } from "Utils/CustomHooks";
import {
  SearchInput,
  EmptyState,
  SideNavigation,
  RadioButton,
  Tag,
  Alert,
} from "@toddle-edu/ds-web";
import {
  NoSearchInFilterIllustration,
  AcademicHonestyIllustration,
  GoalIllustration,
  fontStyles,
} from "@toddle-edu/ds-theme";
import { FILTER_NAME_GRADES } from "Course/Constants/StringConstants";
import {
  getMemoizedFunction,
  generateNestedSelectedData,
  getTopParent,
} from "Utils";
import {
  getPopupDataForSubFields,
  getSideNavigationOptions,
  getFlattenedNodes,
} from "Assessments/modules/Utils";
import { getNodeParents } from "Utils/nodeParentHelpers";
import { buildSearchFlatTree } from "./modules/Utils";
import { isParentTaggingEnabledForType } from "AppComponents/DatasetTaggingBody/modules/Utils";

import PopupForSubFields from "../AddLearningGoals/components/PopupForSubFields";
import TreeNode from "./components/TreeNode";
import FilterDropdownV2 from "UIComponents/FilterDropdownV2";

import ACLStore from "lib/aclStore";
import { Trans } from "react-i18next";
import { ENABLE_MYP_PHASE_SELECTION_RECOMMENDATION } from "Constants/permissionConstants";

import Standards from "./components/Standards";

// relative
import classes from "./TreeView.scss";

const initialVisibleNodes = 50;

const styles = {
  emptyTitleStyle: {
    ...fontStyles.demiBold,
  },
  sideNavigationContainerStyle: {
    height: "100%",
    flex: "0 0 auto",
  },
};

const getRecommendedRootNodes = getMemoizedFunction(
  ({ courseDetails, parentNodes }) => {
    if (!courseDetails?.primaryGrade?.id) return [];
    const recommendedPhase = _.flatMap(
      parentNodes,
      ({ recommendedPhase }) => recommendedPhase
    ).filter(Boolean);

    const gradeId = courseDetails?.primaryGrade?.id;
    const recommendedPhaseIds = [];
    _.forEach(recommendedPhase, ({ gradeId: recommendedGradeId, phaseId }) => {
      if (gradeId === recommendedGradeId) {
        recommendedPhaseIds.push(phaseId);
      }
    });
    return recommendedPhaseIds;
  }
);

const getFilteredRootNodes = getMemoizedFunction(
  ({
    nodes,
    rootNodes,
    showSideNavigation,
    sideNavigationTab,
    groupByType,
    isSubjectPropInNode,
    courseDetails,
  }) => {
    if (!showSideNavigation) {
      return rootNodes;
    }

    if (groupByType === "SUBJECT") {
      return _.filter(rootNodes, id => {
        const node = _.find(nodes, { id }) || {};
        if (isSubjectPropInNode) {
          return node?.subject == sideNavigationTab;
        }
        const subjects = getNodeParents(node, "SUBJECT");
        return _.find(subjects, { id: sideNavigationTab });
      });
    } else {
      return _.filter(rootNodes, id => id == sideNavigationTab);
    }
  }
);

const getFilteredNodesBasedOnGradeIds = ({
  selectedGrades,
  isSubjectPropInNode,
  nodes,
}) => {
  const selectedGradesObj = _.zipObject(selectedGrades, selectedGrades);

  const filterGradesBasedOnCurriculum = isSubjectPropInNode
    ? node => _.some(node?.grades, ({ id }) => selectedGradesObj[id])
    : node =>
        _.some(
          getNodeParents(node, "GRADE"),
          ({ id }) => selectedGradesObj[id]
        );

  return _.filter(nodes, filterGradesBasedOnCurriculum);
};

const getFlattenedNestedNodes = getMemoizedFunction(
  ({
    nodes,
    rootNodes,
    value,
    searchText,
    sideNavigationTab,
    groupByType,
    showSideNavigation,
    showRadioButton,
    selectedRootNode,
    isSubjectPropInNode,
    isPlannerElement,
    selectedGrades,
    showGradesFilter,
    showAllLearningGoals,
    showDataGridUI,
  }) => {
    if (!isPlannerElement) {
      return getFlattenedNodes({ nodes });
    }

    let filteredNodes = nodes;

    if (
      _.size(selectedGrades) > 0 &&
      showGradesFilter &&
      showAllLearningGoals
    ) {
      filteredNodes = getFilteredNodesBasedOnGradeIds({
        selectedGrades,
        isSubjectPropInNode,
        nodes,
      });
    }

    if (!_.isEmpty(searchText)) {
      filteredNodes = buildSearchFlatTree({
        nodes: filteredNodes,
        searchText,
        showDataGridUI,
      });
    }

    const filteredRootNodes = getFilteredRootNodes({
      nodes: filteredNodes,
      rootNodes,
      showSideNavigation,
      sideNavigationTab,
      groupByType,
      isSubjectPropInNode,
    });

    const nestedNodes = generateNestedSelectedData({
      nodes: filteredNodes,
      rootNodes: filteredRootNodes,
      value,
    });

    if (showRadioButton && !_.isEmpty(selectedRootNode)) {
      const filteredNestedNodes =
        _.find(nestedNodes, { id: selectedRootNode }) || {};
      return getFlattenedNodes({
        nodes: _.get(filteredNestedNodes, "children", []),
      });
    }

    return getFlattenedNodes({ nodes: nestedNodes });
  }
);

const getSideNavigationTab = ({ showSideNavigation, parentNodes }) => {
  return showSideNavigation && !_.isEmpty(parentNodes)
    ? _.first(parentNodes)?.id
    : null;
};

const getInitialSelectedRootNodes = ({
  nodes,
  rootNodes,
  showSideNavigation,
  groupByType,
  showRadioButton,
  isSubjectPropInNode,
  selectedValue,
  parentNodes,
  courseDetails,
}) => {
  if (!showRadioButton || !showSideNavigation || _.isEmpty(parentNodes)) {
    return {};
  }

  const initialSelectedRootNodes = {};
  _.forEach(parentNodes, ({ id, recommendedPhase }) => {
    const filteredRootNodes = getFilteredRootNodes({
      nodes,
      rootNodes,
      showSideNavigation,
      sideNavigationTab: id,
      groupByType,
      isSubjectPropInNode,
    });

    if (_.isEmpty(filteredRootNodes)) {
      initialSelectedRootNodes[id] = null;
    } else if (_.isEmpty(selectedValue)) {
      initialSelectedRootNodes[id] = null;
    } else {
      initialSelectedRootNodes[id] =
        _.find(filteredRootNodes, rootId => {
          return _.some(selectedValue, value => {
            const topParent = getTopParent({
              nodeId: value,
              nodes,
            });
            return topParent == rootId;
          });
        }) || null;
    }
    if (
      initialSelectedRootNodes[id] === null &&
      courseDetails?.primaryGrade?.id &&
      ACLStore.can(ENABLE_MYP_PHASE_SELECTION_RECOMMENDATION)
    )
      initialSelectedRootNodes[id] = _.get(
        _.find(recommendedPhase, { gradeId: courseDetails?.primaryGrade?.id }),
        "phaseId",
        null
      );
  });

  return initialSelectedRootNodes;
};

const TreeView = props => {
  const {
    fieldKey,
    fieldLabel,
    value: selectedValue,
    unitSpecificData,
    globalData,
    showSearchFilter,
    showSideNavigation,
    showRadioButton,
    showPopupForSubFields,
    showAllLearningGoals,
    groupByType,
    isSubjectPropInNode,
    isPlannerElement,
    subFields,
    unitPlanFields,
    unitPlanFieldList,
    dataField,
    updateInputField,
    setSubFieldsToUpdate,
    emptyStateSecondaryButton,
    emptyPopupSubText,
    testId,
    getLearningGoalsTagCountTooltip,
    shouldUpdateActiveQuestionOnClick,
    useSubText,
    customBottomElement,
    customTableHeaderElement,
    showGradesFilter,
    courseGradeIds,
    courseDetails,
    plannerElementType,
    showDataGridUI = false,
  } = props;

  const { value, nodes, rootNodes, parentNodes } = showAllLearningGoals
    ? globalData
    : unitSpecificData;

  const { t } = useI18n();

  const selectedValuesRef = useRef(selectedValue);
  const nodesContainerRef = useRef(null);
  const popoverRefs = useRef({});

  const [visibleNodes, setVisibleNodes] = useState(initialVisibleNodes);
  const [searchText, setSearchText] = useState("");
  const [sideNavigationTab, setSideNavigationTab] = useState(() =>
    getSideNavigationTab({ showSideNavigation, parentNodes })
  );
  const [popoverOptionId, setPopoverOptionId] = useState(null);
  const [selectedGrades, setSelectedGrades] = useState([]);
  const [selectedDataGridRows, setSelectedDataGridRows] = useState(
    selectedValue || []
  );

  useEffect(() => {
    if (showGradesFilter) {
      const gradeList = _.get(
        _.find(parentNodes, subject => subject?.id === sideNavigationTab),
        "grades",
        []
      );

      const filteredCourseGradeIds = _.filter(courseGradeIds, id =>
        _.some(gradeList, grade => grade?.id === id)
      );
      setSelectedGrades(filteredCourseGradeIds);
    }
  }, [sideNavigationTab, showGradesFilter]);

  /**
    state object - 
    selectedRootNodes: {
     sideNavigationTabId: phaseObjectiveId
    }

    eg. (for explanation purpose labels are kept in below eg. instead of actual ids) -
    selectedRootNodes: {
      english: Year_1_Objectives
      maths: Year_3_Objectives
    }
    Here english and maths subjects will be in side navigation tabs 
    and Year_1_Objectives and Year_3_Objectives will be selected phase objectives for respective subjects
   */
  const [selectedRootNodes, setSelectedRootNodes] = useState(() =>
    getInitialSelectedRootNodes({
      nodes,
      rootNodes,
      showSideNavigation,
      parentNodes,
      groupByType,
      showRadioButton,
      isSubjectPropInNode,
      selectedValue,
      courseDetails,
    })
  );

  const flattenedNestedNodes = getFlattenedNestedNodes({
    nodes,
    rootNodes,
    value,
    searchText,
    sideNavigationTab,
    groupByType,
    showSideNavigation,
    showRadioButton,
    selectedRootNode: _.get(selectedRootNodes, sideNavigationTab),
    isSubjectPropInNode,
    isPlannerElement,
    selectedGrades,
    showGradesFilter,
    showAllLearningGoals,
    showDataGridUI,
  });

  const rootNodesList = getFilteredRootNodes({
    nodes,
    rootNodes,
    showSideNavigation,
    sideNavigationTab,
    groupByType,
    isSubjectPropInNode,
  });

  const recommendedRootNodesList = getRecommendedRootNodes({
    parentNodes,
    courseDetails,
  });

  const showEmptyStateForGradeFilter =
    _.isEmpty(flattenedNestedNodes) &&
    _.isEmpty(searchText) &&
    showGradesFilter &&
    showAllLearningGoals;
  const showEmptyState =
    _.isEmpty(flattenedNestedNodes) &&
    _.isEmpty(searchText) &&
    (showRadioButton ? _.isEmpty(rootNodesList) : true) &&
    !showEmptyStateForGradeFilter;
  const showSearchEmptyState =
    _.isEmpty(flattenedNestedNodes) && !_.isEmpty(searchText);
  const shouldRenderSideNavigation =
    showSideNavigation && !_.isEmpty(parentNodes) && _.size(parentNodes) > 1;
  const showPopover = showPopupForSubFields && !_.isEmpty(popoverOptionId);
  const showPhaseSelectionEmptyState =
    showRadioButton &&
    _.isEmpty(searchText) &&
    !_.isEmpty(rootNodesList) &&
    (_.isEmpty(flattenedNestedNodes) ||
      _.isEmpty(_.get(selectedRootNodes, sideNavigationTab)));

  const containerClasses = classNames({
    [classes.container]: !showDataGridUI,
    [classes.datagridContainer]: showDataGridUI,
    [classes.fullHeightContainer]: showSearchEmptyState,
  });

  const innerContainerClasses = classNames({
    [classes.innerContainer]: true,
    [classes.innerBorderContainer]: true,
  });

  useDidUpdate(() => {
    // When data is changed from global data to unit specific data or vice versa

    if (showSideNavigation && groupByType !== "SUBJECT") {
      // Reset side navigation tab when tab list are root level nodes...
      // because same root level node might not present when switched from global data to unit specific data
      // eg. in ATLs, Nested elements
      resetSideNavigationTab();
    }
  }, [showAllLearningGoals]);

  const resetSideNavigationTab = () => {
    const updatedSideNavigationTab = getSideNavigationTab({
      showSideNavigation,
      parentNodes,
    });
    setSideNavigationTab(updatedSideNavigationTab);
  };

  const handleSideNavigationClick = tab => {
    setSideNavigationTab(tab);
    setVisibleNodes(initialVisibleNodes);
  };

  const handleUpdateSubFieldsValue = useCallback(() => {
    const selectedValue = selectedValuesRef.current;
    const { popupData, options } = getPopupDataForSubFields({
      unitPlanFieldList,
      fieldKey,
      unitPlanFields,
      dataField,
      subFields,
    });

    const subFieldsToUpdate = {};

    _.forEach(selectedValue, id => {
      const popupDataForCurrentOption = _.get(popupData, id, {});

      _.forEach(popupDataForCurrentOption, ({ editModeValues }, fieldKey) => {
        const currentSubField = _.find(subFields, ({ key }) => key == fieldKey);
        const fieldUid = _.get(currentSubField, "config.filteredBy[0]", null);
        let setKey = "nodes";
        if (_.get(currentSubField, "viewType", "") === "ScopeAndSequence") {
          setKey = "benchmarks";
        }
        const nodes = _.get(options, `${fieldKey}.${setKey}`, []);

        let currentSubFieldObj = _.get(subFieldsToUpdate, fieldKey, {});
        if (_.isEmpty(currentSubFieldObj)) {
          currentSubFieldObj = { fieldUid, value: [], nodes: [] };
        }
        currentSubFieldObj.value = _.uniq([
          ...currentSubFieldObj.value,
          ...editModeValues,
        ]);
        currentSubFieldObj.nodes = _.uniqBy(
          [...currentSubFieldObj.nodes, ...nodes],
          "id"
        );
        subFieldsToUpdate[fieldKey] = currentSubFieldObj;
      });
    });

    // if current field selection is empty clear all subFields selected data
    if (_.isEmpty(subFieldsToUpdate)) {
      _.forEach(popupData, currentPopupData => {
        _.forEach(currentPopupData, (value, fieldKey) => {
          const currentSubField = _.find(
            subFields,
            ({ key }) => key == fieldKey
          );
          const fieldUid = _.get(currentSubField, "config.filteredBy[0]", null);
          subFieldsToUpdate[fieldKey] = { fieldUid, value: [], nodes: [] };
        });
      });
    }

    setSubFieldsToUpdate(subFieldsToUpdate);
  }, []);

  const handleUpdateInputField = useCallback(() => {
    let filteredValues = selectedValuesRef.current;

    if (showRadioButton && !_.isEmpty(filteredValues)) {
      // For each tab(subject), objectives of only last selected root node(phase) should be selected ...
      // even if user selects objectives from multiple phases of same tab(subject)
      // eg. for English subject, if user selected objectives from Year_1 and Year_3 and last selected phase is Year_3 ...
      //     then objectives of only Year_3 should be selected in the final value
      filteredValues = _.filter(filteredValues, value => {
        const topParent = getTopParent({
          nodeId: value,
          nodes: _.get(globalData, "nodes", []),
        });
        return _.includes(_.values(selectedRootNodes), topParent);
      });
    }

    if (showPopupForSubFields) {
      updateInputField(
        _.filter(value, ({ id }) => _.includes(filteredValues, id))
      );
      handleUpdateSubFieldsValue();
      return;
    }

    updateInputField(filteredValues);
  }, [showRadioButton, showPopupForSubFields, selectedRootNodes, globalData]);

  const handleDataGridRowsUpdate = useCallback(selectedRows => {
    setSelectedDataGridRows(selectedRows);
    updateInputField(selectedRows);
  }, []);

  const handleCheckboxClick = useCallback(
    value => {
      let updatedValue = [];
      const currentValue = selectedValuesRef.current;
      if (_.includes(currentValue, value)) {
        updatedValue = _.filter(currentValue, id => id != value);
      } else {
        updatedValue = _.uniq([...currentValue, value]);
      }
      selectedValuesRef.current = updatedValue;
      handleUpdateInputField();
    },
    [handleUpdateInputField]
  );

  const isChecked = useCallback(value => {
    return _.includes(selectedValuesRef.current, value);
  }, []);

  const handleScroll = () => {
    if (showDataGridUI) {
      return;
    }

    const containerDiv = nodesContainerRef.current;

    if (containerDiv) {
      const loadMoreNodes =
        Math.abs(
          containerDiv.scrollHeight -
            containerDiv.scrollTop -
            containerDiv.clientHeight
        ) < 1000 && containerDiv.scrollTop > 0;

      if (loadMoreNodes && visibleNodes < _.size(flattenedNestedNodes)) {
        setVisibleNodes(prev => prev + initialVisibleNodes);
      } else if (containerDiv.scrollTop === 0 && visibleNodes > 100) {
        setVisibleNodes(100);
      }
    }
  };

  const updatePopoverRef = useCallback((node, id) => {
    popoverRefs.current[id] = node;
  }, []);

  const togglePopover = useCallback(id => {
    const updatedOptionId = popoverOptionId == id ? null : id;
    setPopoverOptionId(updatedOptionId);
  }, []);

  const closePopover = () => {
    setPopoverOptionId(null);
  };

  const renderPopover = () => {
    return (
      <PopupForSubFields
        optionId={popoverOptionId}
        subFields={subFields}
        closePopover={closePopover}
        childrenRef={popoverRefs.current[popoverOptionId]}
        unitPlanFieldList={unitPlanFieldList}
        unitPlanFields={unitPlanFields}
        fieldKey={fieldKey}
        dataField={dataField}
        emptyPopupSubText={emptyPopupSubText}
      />
    );
  };

  const getEmptyStateData = () => {
    if (showPhaseSelectionEmptyState) {
      return {
        illustration: AcademicHonestyIllustration,
        title: t("classRoom:select_phase_objective_first"),
        subtitle: null,
      };
    }
    if (showSearchEmptyState) {
      return {
        illustration: NoSearchInFilterIllustration,
        title: t("common:no_results_found_on_search", {
          label: fieldLabel,
        }),
        subtitle: t("studentGoals:try_a_different_keyword"),
      };
    }
    if (showEmptyStateForGradeFilter) {
      return {
        illustration: GoalIllustration,
        title: t("classRoom:no_label_to_select_from", {
          label: fieldLabel,
        }),
        subtitle: t("classRoom:no_label_to_select_from_subtext", {
          label: fieldLabel,
        }),
        secondaryButton: null,
      };
    }
    if (!showAllLearningGoals) {
      return {
        illustration: AcademicHonestyIllustration,
        title: t("classRoom:no_label_selected_in_this_unit", {
          label: fieldLabel,
        }),
        subtitle: null,
        secondaryButton: emptyStateSecondaryButton,
      };
    }
    return {
      illustration: GoalIllustration,
      title: t("classRoom:no_label_to_select_from", {
        label: fieldLabel,
      }),
      subtitle: t("classRoom:no_label_to_select_from_subtext", {
        label: fieldLabel,
      }),
      secondaryButton: null,
    };
  };

  const renderEmptyState = () => {
    const {
      illustration,
      title,
      subtitle,
      secondaryButton,
    } = getEmptyStateData();

    return (
      <div className={classes.emptyStateContainer}>
        <EmptyState
          illustration={illustration}
          title={title}
          subtitle={subtitle}
          titleStyle={styles.emptyTitleStyle}
          secondaryButton={secondaryButton}
        />
      </div>
    );
  };

  const renderRadioButtonList = () => {
    const radioButtonContainerClasses = classNames({
      [classes.radioButtonContainer]: true,
      [classes.radioButtonContainerWithSelection]: !showPhaseSelectionEmptyState,
    });
    const recommendedNodeId = _.intersection(
      recommendedRootNodesList,
      rootNodesList
    );
    let recommendedNode = {};
    if (!_.isEmpty(recommendedNodeId)) {
      recommendedNode = _.find(nodes, { id: recommendedNodeId[0] }) || {};
    }
    const grade = courseDetails?.primaryGrade;

    return (
      <div className={radioButtonContainerClasses}>
        <div className="text-label-1">
          {t("classRoom:select_a_phase_objective")}
        </div>
        <div className={classes.radioButtonList}>
          {_.map(rootNodesList, id => {
            const { label } = _.find(nodes, { id }) || {};
            const isChecked = id == _.get(selectedRootNodes, sideNavigationTab);

            const radioButtonCardClasses = classNames({
              [classes.radioButtonCard]: true,
              [classes.selectedRadioButtonCard]: isChecked,
            });

            const onChange = () => {
              const updatedSelectedRootNodes = {
                ...selectedRootNodes,
                [sideNavigationTab]: id,
              };
              setSelectedRootNodes(updatedSelectedRootNodes);
              const filteredValues = _.filter(
                selectedValuesRef.current,
                value => {
                  const topParent = getTopParent({
                    nodeId: value,
                    nodes: _.get(globalData, "nodes", []),
                  });
                  return _.includes(
                    _.values(updatedSelectedRootNodes),
                    topParent
                  );
                }
              );
              if (!_.isEmpty(filteredValues)) {
                updateInputField(filteredValues);
              }
            };

            return (
              <div className={radioButtonCardClasses} onClick={onChange}>
                <RadioButton
                  key={id}
                  checked={isChecked}
                  onChange={onChange}
                  testId={`${testId}-yearObjectivesPhase`}
                >
                  <div className={classes.labelContainer}>
                    <div className={classes.label}>{label} </div>
                    {ACLStore.can(ENABLE_MYP_PHASE_SELECTION_RECOMMENDATION) &&
                    _.includes(recommendedRootNodesList, id) ? (
                      <Tag dsVersion="2.0" size="xx-small" color="teal">
                        {t("unitPlan:recommended")}
                      </Tag>
                    ) : (
                      ""
                    )}
                  </div>
                </RadioButton>
              </div>
            );
          })}
        </div>
        {ACLStore.can(ENABLE_MYP_PHASE_SELECTION_RECOMMENDATION) &&
          !_.isEmpty(recommendedNode) &&
          recommendedNode.id !==
            _.get(selectedRootNodes, sideNavigationTab) && (
            <Alert
              type={"warning"}
              message={
                <Trans i18nKey="unitPlan:myp_recommended_objective_unit_plan_edit_warning_msg">
                  IB recommends
                  <strong>
                    {{
                      phase: recommendedNode?.label,
                      grade: grade?.name,
                    }}
                  </strong>
                  for students. Please ensure you are aware of the selection
                  made before you proceed.
                </Trans>
              }
            />
          )}
      </div>
    );
  };

  const allowParentTagging = isParentTaggingEnabledForType({
    plannerElementType,
  });

  const renderTreeNodes = () => {
    return (
      <div className={innerContainerClasses}>
        {_.map(_.slice(flattenedNestedNodes, 0, visibleNodes), node => {
          const { id, label, subText, children: childrenNodes, code, depth } =
            node || {};

          return (
            <TreeNode
              key={id}
              value={id}
              label={label}
              subText={subText}
              code={code}
              handleCheckboxClick={handleCheckboxClick}
              depth={depth}
              isNodeSelected={isChecked}
              showPopupForSubFields={showPopupForSubFields}
              updatePopoverRef={updatePopoverRef}
              togglePopover={togglePopover}
              searchText={searchText}
              testId={testId}
              isLeaf={_.isEmpty(childrenNodes)}
              getLearningGoalsTagCountTooltip={getLearningGoalsTagCountTooltip}
              shouldUpdateActiveQuestionOnClick={
                shouldUpdateActiveQuestionOnClick
              }
              useSubText={useSubText}
              allowParentTagging={allowParentTagging}
            />
          );
        })}
      </div>
    );
  };

  const renderDataGridUI = () => {
    return (
      <Standards
        selectedDataGridRows={selectedDataGridRows}
        handleDataGridRowsUpdate={handleDataGridRowsUpdate}
        plannerElementType={plannerElementType}
        flattenedNestedNodes={flattenedNestedNodes}
      />
    );
  };

  const renderGradesFilter = () => {
    const gradeList = _.get(
      _.find(parentNodes, subject => subject?.id === sideNavigationTab),
      "grades",
      []
    );

    const gradeOptions = _.map(gradeList, grade => ({
      value: grade?.id,
      label: grade?.name,
    }));

    const updateGradeFilter = value => {
      setSelectedGrades(value?.grades);
    };

    return (
      <FilterDropdownV2
        name={FILTER_NAME_GRADES}
        options={gradeOptions}
        label="common:grade"
        showSearchBar={true}
        showAllOption={true}
        updateInputField={updateGradeFilter}
        value={selectedGrades}
        noOptionLabel="common:select"
        type="CHECKLIST_DROPDOWN"
        showSelectedOptionsOnTop={true}
      />
    );
  };

  const nodesContainerClasses = classNames({
    [classes.nodesContainer]: true,
    [classes.nodesContainerWithoutScroll]: showDataGridUI,
  });

  return (
    <>
      {shouldRenderSideNavigation ? (
        // ds-2.0 side navigation doesn't support containerStyle
        <div style={styles.sideNavigationContainerStyle}>
          <SideNavigation
            dsVersion="2.0"
            options={getSideNavigationOptions({ parentNodes })}
            selectedOptionKey={sideNavigationTab}
            containerStyle={styles.sideNavigationContainerStyle}
            onClick={handleSideNavigationClick}
            testId={testId}
          />
        </div>
      ) : null}
      {showEmptyState ? (
        renderEmptyState()
      ) : (
        <div
          className={nodesContainerClasses}
          ref={nodesContainerRef}
          onScroll={handleScroll}
        >
          {showRadioButton ? renderRadioButtonList() : null}
          {showPhaseSelectionEmptyState ? renderEmptyState() : null}
          {!showPhaseSelectionEmptyState ? (
            <div className={containerClasses}>
              <div className={classes.headerContainer}>
                {showSearchFilter ? (
                  <div className={classes.searchContainer}>
                    <SearchInput
                      value={searchText}
                      placeholder={t("common:search")}
                      onChange={setSearchText}
                      testId={`${testId}-searchInput`}
                      focusSearchBySlash
                    />
                  </div>
                ) : null}
                {showGradesFilter && showAllLearningGoals
                  ? renderGradesFilter()
                  : null}
              </div>
              {!_.isEmpty(customTableHeaderElement)
                ? customTableHeaderElement
                : null}
              {showSearchEmptyState || showEmptyStateForGradeFilter
                ? renderEmptyState()
                : showDataGridUI
                ? renderDataGridUI()
                : renderTreeNodes()}
              {!_.isEmpty(customBottomElement) ? customBottomElement : null}
            </div>
          ) : null}
        </div>
      )}
      {showPopover ? renderPopover() : null}
    </>
  );
};

TreeView.displayName = "TreeView";

TreeView.defaultProps = {
  showSearchFilter: true,
  showSideNavigation: true,
  showRadioButton: false,
  showPopupForSubFields: false,
  globalData: {},
  unitSpecificData: {},
  isSubjectPropInNode: false,
  updatePopoverRef: () => {},
  togglePopover: () => {},
  getLearningGoalsTagCountTooltip: _.noop,
  shouldUpdateActiveQuestionOnClick: false,
  useSubText: false,
  testId: "IBComponents-treeView",
};

export default TreeView;
